import { Button, Flex, Select, Text } from "@mantine/core";
import { useCallback } from "react";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { usePostApi } from "src/hooks";
import { type ISelectedDraft } from "src/pages/Settings/Miscellaneous/DraftEntries/DraftEntries";
import { ButtonsWrapper } from "src/styled";

interface Props {
  deleted: boolean;
  onStatusChange: (value: string) => void;
  onCheckAll: () => void;
  selected: ISelectedDraft[];
  refetch: () => Promise<void>;
}

const DraftEntriesHeader = ({
  deleted,
  onStatusChange,
  onCheckAll,
  selected,
  refetch,
}: Props) => {
  const { post } = usePostApi();

  const handleDelete = useCallback(async () => {
    await post({
      request: deleted ? "perma-drafts" : "delete-drafts",
      body: { params: { list: selected } },
    });
    await refetch();
  }, [deleted, post, refetch, selected]);

  const handleRestore = useCallback(async () => {
    await post({
      request: deleted ? "restore-deleted-drafts" : "restore-drafts",
      body: { params: { list: selected } },
    });
    await refetch();
  }, [deleted, post, refetch, selected]);

  return (
    <Breadcrumbs border={false}>
      <ButtonsWrapper>
        <ConfirmationButton
          buttonProps={{ variant: "outline", color: "red" }}
          modalText={
            deleted ? (
              <Flex direction="column" gap="lg">
                <Text align="center">
                  Are you sure you want to{" "}
                  <Text component="span" color="red.8">
                    PERMANENTLY
                  </Text>{" "}
                  delete entries?
                </Text>
                <Text align="center">
                  You will not be able to restore these entries once done!
                </Text>
              </Flex>
            ) : (
              "Are you sure you want to delete these draft entries?"
            )
          }
          onConfirm={handleDelete}>
          Delete
        </ConfirmationButton>
        <ConfirmationButton
          buttonProps={{ variant: "outline", color: "green" }}
          modalText="Are you sure you want to restore these entries?"
          onConfirm={handleRestore}>
          Restore
        </ConfirmationButton>
        <Select
          value={deleted ? "deleted" : "drafts"}
          onChange={onStatusChange}
          data={[
            { label: "Drafts", value: "drafts" },
            { label: "Deleted Drafts", value: "deleted" },
          ]}
        />
        <Button variant="outline" onClick={onCheckAll}>
          Check All
        </Button>
      </ButtonsWrapper>
    </Breadcrumbs>
  );
};

export default DraftEntriesHeader;
